"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { GlowCard } from "./Effects";

gsap.registerPlugin(ScrollTrigger);

const STEPS = [
  { num: "01", title: "Install", desc: "Add 8Dfy to Chrome in one click. No sign-up, no permissions fuss.", accent: "#FF3366", icon: "↓" },
  { num: "02", title: "Play something", desc: "Open YouTube or Apple Music. For Spotify Web, click 8Dfy in Chrome’s toolbar once to connect the tab.", accent: "#FF9F1C", icon: "▶" },
  { num: "03", title: "Toggle ON", desc: "Click the floating panel. Sound wraps around you instantly.", accent: "#2EC4B6", icon: "◉" },
];

export default function HowItWorks() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from("[data-step]", {
        scrollTrigger: { trigger: ref.current, start: "top 80%", toggleActions: "play none none none" },
        y: 40, autoAlpha: 0, duration: 0.5, stagger: 0.12, ease: "power2.out",
      });
      gsap.from("[data-step-line]", {
        scrollTrigger: { trigger: ref.current, start: "top 75%", toggleActions: "play none none none" },
        scaleX: 0, duration: 1, ease: "power2.out",
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section id="how" ref={ref} className="section">
      <p className="text-[11px] font-bold uppercase tracking-[2px] text-[#2EC4B6] mb-3">Dead Simple</p>
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">Three steps. That&apos;s it.</h2>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 relative">
        {/* Connecting line (desktop) */}
        <div
          data-step-line
          className="hidden sm:block absolute top-[50px] left-[18%] right-[18%] h-[1px] origin-left"
          style={{ background: "linear-gradient(to right, rgba(255,51,102,0.25), rgba(255,159,28,0.25), rgba(46,196,182,0.25))" }}
        />

        {STEPS.map((step) => (
          <GlowCard
            key={step.num}
            color={step.accent}
            className="rounded-lg"
            style={{ background: "var(--surface)", boxShadow: "var(--shadow-card)" }}
          >
            <div data-step className="p-6 text-center" style={{ visibility: "visible" }}>
              <div
                className="w-[52px] h-[52px] rounded-full mx-auto mb-4 flex items-center justify-center text-lg font-black border transition-all duration-300"
                style={{ borderColor: `${step.accent}35`, color: step.accent, background: `${step.accent}0a` }}
              >
                {step.icon}
              </div>
              <span className="text-[10px] font-bold uppercase tracking-[1.8px] text-[#b3b3b3]/70">{step.num}</span>
              <h3 className="text-base font-bold text-white mt-1">{step.title}</h3>
              <p className="text-sm text-[#b3b3b3] mt-2 leading-relaxed">{step.desc}</p>
            </div>
          </GlowCard>
        ))}
      </div>
    </section>
  );
}
